import React, { useContext, useMemo } from "react";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import { useRacehorse360Api } from "hooks/api";
import { useLoggedInUser } from "./LoggedInUserProvider";

export interface PermissionsContextModel {
  isStallApplicationsPageEnabled: boolean;
  isWorkOrdersPageEnabled: boolean;
  isInventoryAnalyticsPageEnabled: boolean;
  isTrainerWorkoutsPageEnabled: boolean;
  isVetWorkoutsPageEnabled: boolean;
  isTrackOpticsPageEnabled: boolean;
  isLoading: boolean;
}

export const PermissionsContext = React.createContext<PermissionsContextModel>({
  isStallApplicationsPageEnabled: false,
  isWorkOrdersPageEnabled: false,
  isInventoryAnalyticsPageEnabled: false,
  isTrainerWorkoutsPageEnabled: false,
  isVetWorkoutsPageEnabled: false,
  isTrackOpticsPageEnabled: false,
  isLoading: true
});

export const usePermissions = () => useContext(PermissionsContext);

export const PermissionsProvider = props => {
  const { children } = props;
  const { usePullApplicationFeatureFlags } = useRacehorse360Api();
  const { currentUser } = useLoggedInUser();

  const { data, isLoading, isFetching } = usePullApplicationFeatureFlags(
    "PullApplicationSettings",
    {
      keys: [
        racehorse360.ApplicationFeatureKey
          .APPLICATION_FEATURE_KEY_STALL_APPLICATION_FORMS,
        racehorse360.ApplicationFeatureKey.APPLICATION_FEATURE_KEY_WORK_ORDERS,
        racehorse360.ApplicationFeatureKey
          .APPLICATION_FEATURE_KEY_INVENTORY_ANALYTICS,
        racehorse360.ApplicationFeatureKey
          .APPLICATION_FEATURE_KEY_TRAINER_WORKOUT_REQUESTS,
        racehorse360.ApplicationFeatureKey
          .APPLICATION_FEATURE_KEY_VET_WORKOUT_REQUESTS,
        racehorse360.ApplicationFeatureKey.APPLICATION_FEATURE_KEY_TRACK_OPTICS
      ]
    },
    {
      enabled: currentUser.roles.length > 0,
      onError: error => {
        console.error(error);
      }
    }
  );

  const isStallApplicationsPageEnabled =
    data?.featureFlags?.find(
      ff =>
        ff.key ===
        racehorse360.ApplicationFeatureKey
          .APPLICATION_FEATURE_KEY_STALL_APPLICATION_FORMS
    )?.isEnabled === true;
  const isWorkOrdersPageEnabled =
    data?.featureFlags?.find(
      ff =>
        ff.key ===
        racehorse360.ApplicationFeatureKey.APPLICATION_FEATURE_KEY_WORK_ORDERS
    )?.isEnabled === true;
  const isInventoryAnalyticsPageEnabled =
    data?.featureFlags?.find(
      ff =>
        ff.key ===
        racehorse360.ApplicationFeatureKey
          .APPLICATION_FEATURE_KEY_INVENTORY_ANALYTICS
    )?.isEnabled === true;
  const isTrainerWorkoutsPageEnabled =
    data?.featureFlags?.find(
      ff =>
        ff.key ===
        racehorse360.ApplicationFeatureKey
          .APPLICATION_FEATURE_KEY_TRAINER_WORKOUT_REQUESTS
    )?.isEnabled === true;
  const isVetWorkoutsPageEnabled =
    data?.featureFlags?.find(
      ff =>
        ff.key ===
        racehorse360.ApplicationFeatureKey
          .APPLICATION_FEATURE_KEY_VET_WORKOUT_REQUESTS
    )?.isEnabled === true;
  const isTrackOpticsPageEnabled =
    data?.featureFlags?.find(
      ff =>
        ff.key ===
        racehorse360.ApplicationFeatureKey.APPLICATION_FEATURE_KEY_TRACK_OPTICS
    )?.isEnabled === true;

  const contextValue = useMemo(
    () =>
      ({
        isStallApplicationsPageEnabled,
        isWorkOrdersPageEnabled,
        isInventoryAnalyticsPageEnabled,
        isTrainerWorkoutsPageEnabled,
        isVetWorkoutsPageEnabled,
        isTrackOpticsPageEnabled,
        isLoading
      } as PermissionsContextModel),
    [isLoading, isFetching]
  );

  return (
    <PermissionsContext.Provider value={contextValue}>
      {children}
    </PermissionsContext.Provider>
  );
};
